import { useEffect, useState, type ComponentType } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Space, Spin, Steps, Tag, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { registryRecordApi, type RegistryProgram, type RegistryRecord } from '../../api/registryRecordApi';
import { patientApi, type Patient } from '../../api/patientApi';

const { Text } = Typography;

export type PhaseStatus = 'COMPLETE' | 'PARTIAL' | 'MISSING';

export const PHASE_STATUS_META: Record<PhaseStatus, { label: string; color: string }> = {
  COMPLETE: { label: 'Completă', color: 'green' },
  PARTIAL: { label: 'Parțial documentată', color: 'orange' },
  MISSING: { label: 'Nedocumentată', color: 'default' },
};

export interface PhaseDef {
  phase: number;
  title: string;
}

export interface PhasePageProps {
  record: RegistryRecord;
  patient: Patient | null;
  /** statusul fazei curente (calculat de workspace) */
  status: PhaseStatus;
}

/** Configurația unui spațiu de lucru pe faze (hepato / dermato). */
export interface RegistryPhaseWorkspaceConfig {
  program: RegistryProgram;
  /** segmentul de rută: /patients/:id/<routeSegment>/faza-N */
  routeSegment: 'hepato' | 'dermato';
  phases: readonly PhaseDef[];
  computeStatuses: (record: RegistryRecord) => Record<number, PhaseStatus>;
  pages: Record<number, ComponentType<PhasePageProps>>;
  /** mesajul din starea goală (fără fișă de registru importată) */
  emptyDescription: string;
  emptyTitle: string;
}

/**
 * Șablonul spațiului de lucru pe faze: sidebar cu fazele și statusurile lor +
 * pagina fazei active. Datele de registru se încarcă o singură dată.
 */
export default function RegistryPhaseWorkspace({
  config,
  phase,
}: {
  config: RegistryPhaseWorkspaceConfig;
  phase: number;
}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<RegistryRecord | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    registryRecordApi
      .get(config.program, id)
      .then((r) => !cancelled && setRecord(r))
      .catch(() => !cancelled && setError('Fișa de registru nu a putut fi încărcată'))
      .finally(() => !cancelled && setLoading(false));
    // Datele pacientului (nume, număr de registru) — opționale pentru antet
    patientApi
      .getById(id)
      .then((p) => !cancelled && setPatient(p))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [id, config.program]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Se încarcă fișa de registru…</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return <Alert type="error" showIcon message={error} />;
  }

  if (!record) {
    return (
      <Card title={config.emptyTitle}>
        <Empty description={config.emptyDescription} />
        <div style={{ textAlign: 'center' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/patients/${id}`)}>
            Înapoi la fișa pacientului
          </Button>
        </div>
      </Card>
    );
  }

  const statuses = config.computeStatuses(record);
  const PhasePage = config.pages[phase];

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <Card
        size="small"
        style={{ width: 300, flexShrink: 0 }}
        title={
          <Space direction="vertical" size={0}>
            <Text strong>{patient ? `${patient.lastName} ${patient.firstName}` : 'Pacient'}</Text>
            {patient?.registryNumber && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Nr. registru: {patient.registryNumber}
              </Text>
            )}
          </Space>
        }
        extra={
          <Button size="small" type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/patients/${id}`)}>
            Fișa pacientului
          </Button>
        }
      >
        {config.phases.map((p) => {
          const meta = PHASE_STATUS_META[statuses[p.phase] ?? 'MISSING'];
          const active = p.phase === phase;
          return (
            <div
              key={p.phase}
              role="button"
              tabIndex={0}
              data-testid={`phase-nav-${p.phase}`}
              onClick={() => navigate(`/patients/${id}/${config.routeSegment}/faza-${p.phase}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/patients/${id}/${config.routeSegment}/faza-${p.phase}`)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                marginBottom: 4,
                borderRadius: 6,
                cursor: 'pointer',
                background: active ? '#e6f4ff' : undefined,
                border: active ? '1px solid #91caff' : '1px solid transparent',
              }}
            >
              <Text strong={active}>
                Faza {p.phase} — {p.title}
              </Text>
              <Tag color={meta.color} data-testid={`phase-status-${p.phase}`} style={{ marginInlineEnd: 0 }}>
                {meta.label}
              </Tag>
            </div>
          );
        })}
      </Card>
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Progresul pe faze — complementar sidebar-ului, clickable */}
        <Steps
          size="small"
          current={phase - 1}
          onChange={(i) => navigate(`/patients/${id}/${config.routeSegment}/faza-${i + 1}`)}
          style={{ marginBottom: 16 }}
          items={config.phases.map((p) => ({
            title: `Faza ${p.phase}`,
            status:
              p.phase === phase
                ? ('process' as const)
                : statuses[p.phase] === 'COMPLETE'
                  ? ('finish' as const)
                  : statuses[p.phase] === 'PARTIAL'
                    ? ('process' as const)
                    : ('wait' as const),
          }))}
        />
        {PhasePage && <PhasePage record={record} patient={patient} status={statuses[phase] ?? 'MISSING'} />}
      </div>
    </div>
  );
}
